/**
 * Simplify a GeoJSON Polygon or MultiPolygon using Douglas–Peucker
 * @param {Object} geojson - GeoJSON Polygon or MultiPolygon
 * @param {number} [tolerance=0.0001] - Distance tolerance (in coordinate units)
 * @returns {Object} Simplified GeoJSON
 */
function simplifyGeoJSON(geojson, tolerance = 0.0001) {
  // Compute perpendicular distance from point p to line segment (a,b)
  function perpendicularDistance(p, a, b) {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    if (dx === 0 && dy === 0) {
      // a and b are the same point
      return Math.hypot(p[0] - a[0], p[1] - a[1]);
    }
    const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (dx * dx + dy * dy);
    const projX = a[0] + t * dx;
    const projY = a[1] + t * dy;
    return Math.hypot(p[0] - projX, p[1] - projY);
  }

  // Recursive Douglas–Peucker algorithm
  function douglasPeucker(points, tolerance) {
    if (points.length <= 2) return points;

    let maxDist = 0;
    let index = 0;
    for (let i = 1; i < points.length - 1; i++) {
      const dist = perpendicularDistance(points[i], points[0], points[points.length - 1]);
      if (dist > maxDist) {
        index = i;
        maxDist = dist;
      }
    }

    if (maxDist > tolerance) {
      const left = douglasPeucker(points.slice(0, index + 1), tolerance);
      const right = douglasPeucker(points.slice(index), tolerance);
      return left.slice(0, -1).concat(right);
    } else {
      return [points[0], points[points.length - 1]];
    }
  }

  // Apply simplification to a closed polygon ring
  function simplifyRing(ring) {
    if (ring.length <= 4) return ring; // keep small polygons intact

    const closed = ring[0][0] === ring[ring.length - 1][0] &&
                   ring[0][1] === ring[ring.length - 1][1];

    const simplified = douglasPeucker(ring, tolerance);

    // ensure closure
    if (!closed) {
      simplified.push(simplified[0]);
    } else if (
      simplified[0][0] !== simplified[simplified.length - 1][0] ||
      simplified[0][1] !== simplified[simplified.length - 1][1]
    ) {
      simplified.push(simplified[0]);
    }

    return simplified;
  }

  if (geojson.type === "Polygon") {
    return {
      ...geojson,
      coordinates: geojson.coordinates.map(simplifyRing)
    };
  } else if (geojson.type === "MultiPolygon") {
    return {
      ...geojson,
      coordinates: geojson.coordinates.map(poly =>
        poly.map(simplifyRing)
      )
    };
  } else {
    throw new Error("Only Polygon or MultiPolygon supported");
  }
}

export { simplifyGeoJSON };
